import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import TransactionService from "../../../services/api/transaction.service";
import KycService from "../../../services/api/kyc.service";
import referralService from "../../../services/api/referral.service";
import Balance from "../../features/Wallet/Balance";
import LowBalanceNotification from "../../../components/static/Admindashboard/LowBalenceNotification";
import { useNavigation } from "@/contexts/notification/NavigationContext";
import { Loader } from "lucide-react";

const TransactionPage = ({ isAdmin = false }) => {
  const [walletData, setWalletData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [kycStatus, setKycStatus] = useState(null);
  const [adminWalletData, setAdminWalletData] = useState(null);
  const [referralProcessing, setReferralProcessing] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const returnData = location.state || {};
  const { pendingTransaction, clearPendingPurchase, getLastMeaningfulPage } =
    useNavigation();
  const [searchParams] = useSearchParams();

  const handleTransactionComplete = () => {
    if (pendingTransaction?.type === "PRODUCT_PURCHASE") {
      navigate(`/company_info/${pendingTransaction.productId}`, {
        state: {
          fromWallet: true,
          completedAction: "add_funds",
        },
      });
    } else if (pendingTransaction?.type === "MUTUAL_FUND_INVESTMENT") {
      navigate(`/mutual-funds/${pendingTransaction.schemeCode}`, {
        state: {
          fromWallet: true,
          completedAction: "add_funds",
        },
      });
    } else if (pendingTransaction?.type === "REFERRAL_PAYOUT") {
      // Handle referral payout completion
      navigate(`/admin/referrals`, {
        state: {
          fromWallet: true,
          completedAction: "referral_payout",
        },
      });
    } else {
      navigate(getLastMeaningfulPage());
    }

    clearPendingPurchase();
  };

  // Fetch admin wallet data if admin
  const fetchAdminWalletData = async () => {
    if (!isAdmin) return;

    try {
      const response = await referralService.getAdminWalletBalance();
      if (response.success) {
        setAdminWalletData(response.data);
      }
    } catch (err) {
      console.error("Error fetching admin wallet:", err);
    }
  };

  useEffect(() => {
    if (!isAdmin) {
      checkKycStatus();
    }
    fetchWalletData();
    fetchAdminWalletData();

    // Load Razorpay script
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, [isAdmin]);

  const checkKycStatus = async () => {
    try {
      const response = await KycService.checkKycStatus();

      if (response.status === "success") {
        setKycStatus(response.data);
      } else {
        setError("Failed to verify KYC status");
      }
    } catch (err) {
      setError("Failed to verify your KYC status");
    }
  };

  const fetchWalletData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await TransactionService.getWalletBalance();

      if (response.success) {
        setWalletData(response.data);
      } else if (response.error === "Wallet not found") {
        const createResponse = await TransactionService.createWallet();
        if (createResponse.success) {
          setWalletData(createResponse.data);
        } else {
          setError(createResponse.error || "Failed to create wallet");
        }
      } else {
        setError(response.error || "Unknown error occurred");
      }
    } catch (err) {
      setError("Failed to connect to the server");
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteKyc = () => {
    navigate("/profile/kyc");
  };

  // Handle referral payout processing
  const handleReferralPayout = async (referralData) => {
    setReferralProcessing(true);
    try {
      const payoutData = {
        amount: referralData.payoutAmount,
        refereeId: referralData.refereeId,
        referrerId: referralData.referrerId,
        adminWalletId: adminWalletData?.walletId,
      };

      const response = await referralService.processReferralPayout(
        referralData.referralTrackingId,
        payoutData
      );

      if (response.success) {
        // Refresh wallet data after successful payout
        await fetchWalletData();
        await fetchAdminWalletData();

        // Show success message or navigate
        navigate("/admin/referrals", {
          state: {
            message: "Referral payout processed successfully",
            type: "success",
          },
        });
      } else {
        setError(response.error || "Failed to process referral payout");
      }
    } catch (err) {
      setError("Failed to process referral payout");
    } finally {
      setReferralProcessing(false);
    }
  };

  const renderPendingTransactionBanner = () => {
    const action = searchParams.get("action");
    const source = searchParams.get("source");

    if (!pendingTransaction || action !== "add_funds") {
      return null;
    }

    // Handle referral payout banner
    if (pendingTransaction.type === "REFERRAL_PAYOUT") {
      return (
        <div className="mb-6 p-4 rounded-lg bg-green-50 border border-green-300">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <svg
                className="w-6 h-6 text-green-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                />
              </svg>
              <div>
                <h3 className="font-semibold text-gray-800">
                  Process Referral Payout
                </h3>
                <p className="text-gray-700">
                  {`Deduct ₹${pendingTransaction.payoutAmount.toLocaleString(
                    "en-IN",
                    { minimumFractionDigits: 2 }
                  )} for referral reward to ${pendingTransaction.referrerName}`}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handleReferralPayout(pendingTransaction)}
                disabled={referralProcessing}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 transition-colors"
              >
                {referralProcessing ? "Processing..." : "Confirm Payout"}
              </button>
              <button
                onClick={() => navigate("/admin/referrals")}
                className="px-4 py-2 border border-green-600 text-green-600 rounded hover:bg-green-50 transition-colors"
              >
                Back to Referrals
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Handle product purchase banner
    if (pendingTransaction.type === "PRODUCT_PURCHASE") {
      return (
        <div className="mb-6 p-4 rounded-lg bg-blue-50 border border-blue-300">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <svg
                className="w-6 h-6 text-blue-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                ></path>
              </svg>
              <div>
                <h3 className="font-semibold text-gray-800">
                  Complete Your Purchase
                </h3>
                <p className="text-gray-700">
                  {`Add ₹${pendingTransaction.totalCost.toLocaleString(
                    "en-IN",
                    {
                      minimumFractionDigits: 2,
                    }
                  )} to purchase ${pendingTransaction.quantity} ${
                    pendingTransaction.productTitle
                    }`}
                </p>
              </div>
            </div>
            <button
              onClick={() =>
                navigate(`/company_info/${pendingTransaction.productId}`)
              }
              className="px-4 py-2 border border-blue-600 text-blue-600 rounded hover:bg-blue-50 whitespace-nowrap transition-colors"
            >
              Return to Product
            </button>
          </div>
        </div>
      );
    } else if (pendingTransaction.type === "MUTUAL_FUND_INVESTMENT") {
      return (
        <div className="mb-6 p-4 rounded-lg bg-blue-50 border border-blue-300">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <svg
                className="w-6 h-6 text-blue-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                ></path>
              </svg>
              <div>
                <h3 className="font-semibold text-gray-800">
                  Complete Your Investment
                </h3>
                <p className="text-gray-700">
                  {`Add ₹${pendingTransaction.amount.toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                  })} to invest in ${pendingTransaction.schemeName}`}
                </p>
              </div>
            </div>
            <button
              onClick={() =>
                navigate(`/mutual-funds/${pendingTransaction.schemeCode}`)
              }
              className="px-4 py-2 border border-blue-600 text-blue-600 rounded hover:bg-blue-50 whitespace-nowrap transition-colors"
            >
              Return to Scheme
            </button>
          </div>
        </div>
      );
    }

    return null;
  };

  // Add this useEffect to handle transaction return logic
  useEffect(() => {
    const completedAction = searchParams.get("completed");
    const returnFromAddFunds = searchParams.get("return_from") === "add_funds";

    if (
      (completedAction === "add_funds" || returnFromAddFunds) &&
      pendingTransaction
    ) {
      if (!loading && walletData) {
        const hasSufficientFunds =
          pendingTransaction.type === "PRODUCT_PURCHASE"
            ? walletData.balance >= pendingTransaction.totalCost
            : pendingTransaction.type === "MUTUAL_FUND_INVESTMENT"
              ? walletData.balance >= pendingTransaction.amount
              : pendingTransaction.type === "REFERRAL_PAYOUT"
                ? (adminWalletData?.balance || 0) >= pendingTransaction.payoutAmount
                : false;

        if (hasSufficientFunds) {
          handleTransactionComplete();
        }
      }
    }
  }, [loading, walletData, adminWalletData, pendingTransaction, searchParams]);

  const renderKycBanner = () => {
    if (!kycStatus || isAdmin) return null;

    const {
      isKycComplete,
      kycStatus: status,
      missingItems,
      rejectionReason,
      completionPercentage,
    } = kycStatus;

    if (isKycComplete) return null;

    let bannerClass = "mb-6 p-4 rounded-lg ";
    let message = "";
    let actionLabel = "Complete KYC";
    let icon = null;

    switch (status) {
      case "not_started":
        bannerClass += "bg-yellow-50 border border-yellow-300";
        message = "Please complete KYC verification to access all features.";
        icon = (
          <svg
            className="w-6 h-6 text-yellow-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        );
        break;
      case "pending":
        bannerClass += "bg-blue-50 border border-blue-300";
        if (completionPercentage === 100) {
          message =
            "Your KYC verification is pending approval. Some features may be limited until verification is complete.";
          actionLabel = "View KYC Status";
          icon = (
            <svg
              className="w-6 h-6 text-blue-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          );
        } else {
          message = `Your KYC verification is ${completionPercentage}% complete. Some features may be limited until verification is complete.`;
          if (missingItems?.length > 0) {
            message += ` Missing: ${missingItems.join(", ")}.`;
            actionLabel = "Complete Missing Details";
          }
          icon = (
            <svg
              className="w-6 h-6 text-blue-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          );
        }
        break;
      case "rejected":
        bannerClass += "bg-red-50 border border-red-300";
        message = `Your KYC was rejected. Reason: ${
          rejectionReason || "Unknown reason"
          }. Please update and resubmit.`;
        actionLabel = "Update KYC";
        icon = (
          <svg
            className="w-6 h-6 text-red-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        );
        break;
      default:
        bannerClass += "bg-yellow-50 border border-yellow-300";
        message = "KYC verification is required to access all features.";
        icon = (
          <svg
            className="w-6 h-6 text-yellow-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        );
    }

    return (
      <div className={bannerClass}>
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-start gap-3">
            {icon}
            <div>
              <h3 className="font-semibold text-gray-800">
                KYC Verification Required
              </h3>
              <p className="text-gray-700">{message}</p>
            </div>
          </div>
          <button
            onClick={handleCompleteKyc}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 whitespace-nowrap transition-colors"
          >
            {actionLabel}
          </button>
        </div>
        {completionPercentage !== undefined && completionPercentage < 100 && (
          <div className="mt-3">
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className="bg-blue-600 h-2.5 rounded-full transition-all duration-500"
                style={{ width: `${completionPercentage}%` }}
              ></div>
            </div>
            <p className="text-xs text-gray-600 mt-1">
              KYC Completion: {completionPercentage}%
            </p>
          </div>
        )}
      </div>
    );
  };

  // Function to check if transaction features should be restricted
  const isTransactionRestricted = () => {
    if (isAdmin) return false; // Admin doesn't have KYC restrictions
    if (!kycStatus) return true;
    return !kycStatus.isKycComplete;
  };

  return (
    <div className="container mx-auto px-4 py-8 mt-5">
      {/* Low Balance Notification for Admin */}
      {isAdmin && <LowBalanceNotification isAdmin={true} />}

      <h1 className="text-3xl font-bold mb-6">
        {isAdmin ? "Admin Transaction Dashboard" : "Transaction Dashboard"}
      </h1>

      {renderKycBanner()}
      {renderPendingTransactionBanner()}

      {/* Admin Wallet Summary */}
      {isAdmin && adminWalletData && (
        <div className="mb-6 p-4 rounded-lg bg-gray-50 border border-gray-200">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="font-semibold text-gray-800">
                Admin Wallet Balance
              </h3>
              <p className="text-2xl font-bold text-green-600">
                ₹
                {adminWalletData.balance.toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                })}
              </p>
              <p className="text-sm text-gray-600">
                Available for referral payouts and operations
              </p>
            </div>
            <div className="text-right">
              <button
                onClick={() => navigate("/admin/wallet/add-funds")}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors mb-2 block"
              >
                Add Funds
              </button>
              <button
                onClick={() => navigate("/admin/referrals")}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors block"
              >
                Manage Referrals
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8">
          <Loader className="animate-spin mx-auto mb-4" size={48} />
          <p className="mt-2">Setting up your wallet...</p>
        </div>
      ) : (
        <div className="space-y-6">
          <Balance
            walletData={walletData}
            error={error}
            onWalletUpdate={fetchWalletData}
            isRestricted={isTransactionRestricted()}
            kycStatus={kycStatus}
            onCompleteKyc={handleCompleteKyc}
            isAdmin={isAdmin}
            adminWalletData={adminWalletData}
            onAdminWalletUpdate={fetchAdminWalletData}
          />

          {/* Referral Processing Status */}
          {referralProcessing && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-3">
                <Loader className="animate-spin" size={20} />
                <p className="text-blue-800">Processing referral payout...</p>
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800">{error}</p>
              <button
                onClick={() => setError(null)}
                className="mt-2 text-sm text-red-600 hover:text-red-800"
              >
                Dismiss
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TransactionPage;
